import { type ErrorInjection, createErrorInjector } from "./error-injection";
import { type MockOperations, createOperationTracker } from "./observable";

interface MockKVEntry {
	value: string;
	expiration?: number;
	metadata?: unknown;
}

/**
 * In-memory KVNamespace mock for unit testing.
 * Map-based storage with expiration support.
 */
export function createMockKV(
	initialData?: Record<string, unknown>,
): KVNamespace & { _store: Map<string, MockKVEntry> } & MockOperations & ErrorInjection {
	const store = new Map<string, MockKVEntry>();
	const tracker = createOperationTracker();
	const injector = createErrorInjector();

	if (initialData) {
		for (const [key, value] of Object.entries(initialData)) {
			store.set(key, {
				value: typeof value === "string" ? JSON.stringify(value) : JSON.stringify(value),
			});
		}
	}

	return {
		_store: store,
		get operations() {
			return tracker.operations;
		},
		reads: tracker.reads.bind(tracker),
		writes: tracker.writes.bind(tracker),
		deletes: tracker.deletes.bind(tracker),
		reset: tracker.reset.bind(tracker),
		failAfter: injector.failAfter.bind(injector),
		failOn: injector.failOn.bind(injector),
		withLatency: injector.withLatency.bind(injector),
		clearInjections: injector.clearInjections.bind(injector),

		async get(key: string, options?: any): Promise<any> {
			await injector._check(key);
			tracker._record("read", key);
			const entry = store.get(key);
			if (!entry) return null;
			if (entry.expiration && entry.expiration < Date.now() / 1000) {
				store.delete(key);
				return null;
			}
			const type = typeof options === "string" ? options : (options?.type ?? "text");
			if (type === "json") return JSON.parse(entry.value);
			return entry.value;
		},

		async getWithMetadata(key: string, options?: any): Promise<any> {
			await injector._check(key);
			tracker._record("read", key);
			const entry = store.get(key);
			if (!entry) return { value: null, metadata: null, cacheStatus: null };
			if (entry.expiration && entry.expiration < Date.now() / 1000) {
				store.delete(key);
				return { value: null, metadata: null, cacheStatus: null };
			}
			const type = typeof options === "string" ? options : (options?.type ?? "text");
			const value = type === "json" ? JSON.parse(entry.value) : entry.value;
			return { value, metadata: entry.metadata ?? null, cacheStatus: null };
		},

		async put(key: string, value: any, options?: any): Promise<void> {
			await injector._check(key);
			tracker._record("write", key);
			const entry: MockKVEntry = {
				value: typeof value === "string" ? value : JSON.stringify(value),
			};
			if (options?.expiration) entry.expiration = options.expiration;
			if (options?.expirationTtl)
				entry.expiration = Math.floor(Date.now() / 1000) + options.expirationTtl;
			if (options?.metadata) entry.metadata = options.metadata;
			store.set(key, entry);
		},

		async delete(key: string): Promise<void> {
			await injector._check(key);
			tracker._record("delete", key);
			store.delete(key);
		},

		async list(options?: any): Promise<any> {
			await injector._check();
			tracker._record("list");
			const prefix = options?.prefix ?? "";
			const limit = options?.limit ?? 1000;
			const entries = [...store.entries()]
				.filter(([k]) => k.startsWith(prefix))
				.sort(([a], [b]) => a.localeCompare(b));

			const startIndex = options?.cursor ? Number.parseInt(options.cursor, 10) : 0;
			const page = entries.slice(startIndex, startIndex + limit);
			const endIndex = startIndex + page.length;
			const listComplete = endIndex >= entries.length;

			return {
				keys: page.map(([name, entry]) => ({
					name,
					expiration: entry.expiration,
					metadata: entry.metadata,
				})),
				list_complete: listComplete,
				cursor: listComplete ? undefined : String(endIndex),
				cacheStatus: null,
			};
		},
	} as any;
}
